import { useState, useMemo, useEffect } from "react";
import { toast } from "sonner";
import { validateNewProjectStep, focusNewProjectField } from "@/lib/new-project-validation";
import type { MatchedBudget } from "@/components/new-project/PdfOfferUpload";
import type { DBCustomer } from "@/lib/db-types";
import {
  DEFAULT_INVOICE_TERMS,
  isInvoiceTermsValid,
  type InvoiceTerm,
} from "@/components/offer/InvoiceTermsEditor";

export interface SelectedSalesUnit {
  salesUnitId: string;
  ownershipPct: number;
  /** Cached medlemmer ved valgtidspunkt — brukes til fan-out av project_owners. */
  members: { user_id: string }[];
}

export interface BudgetLine {
  id: string;
  phase: string;
  description: string;
  quantity_hours: number;
  amount: number;
  estimated_cost: number;
  has_time: boolean;
  /** Effektiv cost_type fra mal-match — fallback til has_time-heuristikk */
  cost_type: "internal" | "external";
  /** Mal-id hvis linja matchet en offer_line_template (VIB-584) */
  template_id: string | null;
  /** Cached navn fra mal-match for UI-visning. */
  template_name: string | null;
  /**
   * VIB-995: tilvalg (opsjon fra tilbudet). Settes ved konvertering for linjer
   * som tilhørte en offer_sections-rad (section_type='option'). Vises i egen
   * Tilvalg-rubrikk på timetildeling.
   */
  is_optional?: boolean;
  /** VIB-1158: navn på tilvalgs-seksjonen (for per-option rubrikk på timetildeling). */
  option_label?: string | null;
  /** VIB-2866: rekkefølge innen fase fra tilbudet (offer_lines.sort_order). */
  sort_order?: number | null;
}

interface UseNewProjectFormProps {
  customers: DBCustomer[] | undefined;
  nextCode: string;
  /**
   * Valgfri start-state. Brukes av ConvertOfferDialog for å prefille fra et
   * tilbud — kunde, prosjektnavn, budsjettlinjer, fakturaterminer osv.
   */
  initialState?: Partial<NewProjectInitialState>;
  /**
   * VIB-694: når true skipper handleNext step 3 (Tilbud og budsjett) på samme
   * måte som hourly-jobber gjør. ConvertOfferDialog setter denne fordi
   * budsjettlinjene er importert fra tilbudet og ikke skal redigeres i wizarden.
   */
  skipBudgetStep?: boolean;
}

export interface NewProjectInitialState {
  customerId: string;
  projectName: string;
  totalAmount: string;
  budgetLines: BudgetLine[];
  offerOverheadPct: number;
  offerDiscountPct: number;
  offerDiscountAmount: number;
  invoiceTerms: InvoiceTerm[];
  /** VIB-993: reise faktureres separat (medgått) — overført fra tilbudet. */
  billsTravelPerMedgatt: boolean;
  /**
   * VIB-2086: kopieres fra tilbudet ved konvertering. false = gammel OH-formel.
   */
  includeTravelInOverhead?: boolean;
  /** Skip-flag fra wizard — true når Tilbud/budsjett-steget er forhåndsutfylt. */
  pdfUploaded: boolean;
}

export function useNewProjectForm({
  customers,
  nextCode,
  initialState,
  skipBudgetStep = false,
}: UseNewProjectFormProps) {
  const [step, setStep] = useState(0);
  const [stepErrors, setStepErrors] = useState<string[]>([]);

  // Step 0
  const [customerId, setCustomerId] = useState(initialState?.customerId ?? "");
  const [projectName, setProjectName] = useState(initialState?.projectName ?? "");
  const [projectCode, setProjectCode] = useState(nextCode);
  const [totalAmount, setTotalAmount] = useState(initialState?.totalAmount ?? "");
  const [deadline, setDeadline] = useState("");
  // VIB-1297: estimert oppstartsdato — valgfri, ingen validering (i motsetning
  // til deadline som er obligatorisk for eksterne prosjekter).
  const [estimatedStartDate, setEstimatedStartDate] = useState("");
  const [isInternal, setIsInternal] = useState(false);
  // VIB-666: 'fixed' (default) = vanlig tilbud-prosjekt. 'hourly' = time-jobb
  // som hopper over Tilbud/budsjett-steget og ikke krever totalbeløp.
  const [projectType, setProjectType] = useState<"fixed" | "hourly">("fixed");
  const [projectStatus, setProjectStatus] = useState("active");
  const [productionLeadId, setProductionLeadId] = useState("");
  const [pdfUploaded, setPdfUploaded] = useState(initialState?.pdfUploaded ?? false);
  const [budgetLines, setBudgetLines] = useState<BudgetLine[]>(initialState?.budgetLines ?? []);
  const [aiParsedValues, setAiParsedValues] = useState<Record<string, { amount: number }>>({});
  const [aiProgress, setAiProgress] = useState(0);
  const [aiLoading, setAiLoading] = useState(false);
  const [offerOverheadPct, setOfferOverheadPct] = useState(initialState?.offerOverheadPct ?? 0);
  const [offerDiscountPct, setOfferDiscountPct] = useState(initialState?.offerDiscountPct ?? 0);
  const [offerDiscountAmount, setOfferDiscountAmount] = useState(initialState?.offerDiscountAmount ?? 0);

  // Step 1
  const [selectedSalesUnits, setSelectedSalesUnits] = useState<SelectedSalesUnit[]>([]);

  // Step 2: VIB-686 — fleksible faktureringsvilkår (samme datamodell som
  // tilbudsgeneratoren). Default 60/40 oppstart/levering, men admin kan
  // legge til vilkårlig antall milepæl-fakturaer. Sum må være 100 %.
  const [invoiceTerms, setInvoiceTerms] = useState<InvoiceTerm[]>(
    initialState?.invoiceTerms ?? DEFAULT_INVOICE_TERMS,
  );
  const [paymentDueDays, setPaymentDueDays] = useState(14);
  const [invoiceEmail, setInvoiceEmail] = useState("");
  const [customerReferencePerson, setCustomerReferencePerson] = useState("");
  const [customerReferenceNumber, setCustomerReferenceNumber] = useState("");
  // VIB-909: PL-styrt flagg — reise faktureres separat (medgått). Default off.
  // VIB-993: forhåndsutfylles fra tilbudet ved konvertering.
  const [billsTravelPerMedgatt, setBillsTravelPerMedgatt] = useState(
    initialState?.billsTravelPerMedgatt ?? false,
  );
  const [includeTravelInOverhead] = useState(
    initialState?.includeTravelInOverhead ?? true,
  );

  // Prosjektkode er server-autoritativ — sync alltid fra predikert nextCode
  // slik at UI viser riktig serie når intern-toggle endres.
  useEffect(() => {
    if (nextCode) setProjectCode(nextCode);
  }, [nextCode]);

  // VIB-943: T&M-/time-jobber faktureres etterskuddsvis per medgått tid →
  // faktureringsvilkår låst til én 100% sluttfaktura (ingen oppstart/delfaktura).
  useEffect(() => {
    if (projectType === "hourly") {
      setInvoiceTerms([{ label: "Sluttfaktura", pct: 100 }]);
    }
  }, [projectType]);

  const selectedCustomer = useMemo(
    () => customers?.find(c => c.id === customerId) ?? null,
    [customers, customerId]
  );

  const total = Number(totalAmount) || 0;
  // Oppstartsbeløp = første termin (typisk "Oppstart") × kontraktssum.
  // Brukes som visningstall i UI og som backward-compat startup_invoice_pct
  // når prosjektet opprettes.
  const startupPct = invoiceTerms[0]?.pct ?? 0;
  const startAmount = (total * startupPct) / 100;
  const invoiceTermsValid = isInvoiceTermsValid(invoiceTerms);
  const budgetTotal = budgetLines.reduce((s, l) => s + l.amount, 0);
  const totalEstCost = budgetLines.reduce((s, l) => s + l.estimated_cost, 0);
  const totalHours = budgetLines.reduce((s, l) => s + l.quantity_hours, 0);
  const effectiveDiscount =
    offerDiscountAmount > 0
      ? offerDiscountAmount
      : offerDiscountPct > 0 ? (budgetTotal * (1 + offerOverheadPct / 100) * offerDiscountPct) / 100 : 0;
  const calculatedTotal = budgetTotal + (budgetTotal * offerOverheadPct) / 100 - effectiveDiscount;
  const validationDiff = total > 0 && budgetTotal > 0 ? Math.abs(total - calculatedTotal) : 0;

  const validateStep = (s: number): boolean => {
    // VIB-2546: reglene bor i src/lib/new-project-validation.ts; første feil
    // får fokus i tillegg til toast, så tastaturbrukere ser hvor det stopper.
    const errors = validateNewProjectStep(s, {
      isInternal,
      customerId,
      projectName,
      projectType,
      totalAmount,
      deadline,
      productionLeadId,
      invoiceTermsValid,
    });
    setStepErrors(errors.map((e) => e.message));
    if (errors.length > 0) {
      toast.error(errors[0].message);
      focusNewProjectField(errors[0].field);
      return false;
    }
    return true;
  };

  const handleNext = () => {
    if (validateStep(step)) {
      setStepErrors([]);
      // VIB-666: time-jobber hopper over "Tilbud og budsjett" (step 3) — det
      // gir ikke mening å ha tilbud-pdf eller estimerte timer på en T&M-jobb.
      // VIB-694: ConvertOfferDialog skipper også steg 3 (budsjettet er allerede
      // satt fra tilbudet og redigeres i Timetildeling-tabben etterpå).
      // Internprosjekt: hopper alle mellomsteg (Prosjekteiere, Fakturering,
      // Budsjett) — PL settes automatisk som eier 100% i wizarden.
      const nextStep = step + 1;
      const skipBudget = projectType === "hourly" || skipBudgetStep || isInternal;
      if (isInternal && nextStep === 1) {
        setStep(4);
      } else if (skipBudget && nextStep === 3) {
        setStep(4);
      } else {
        setStep(nextStep);
      }
    }
  };

  const toggleSalesUnit = (unit: { id: string; members: { user_id: string }[] }) => {
    setSelectedSalesUnits(prev => {
      if (prev.find(o => o.salesUnitId === unit.id)) {
        const remaining = prev.filter(o => o.salesUnitId !== unit.id);
        if (remaining.length === 0) return [];
        const evenSplit = Math.floor(100 / remaining.length);
        const remainder = 100 - evenSplit * remaining.length;
        return remaining.map((o, i) => ({
          ...o,
          ownershipPct: evenSplit + (i === 0 ? remainder : 0),
        }));
      }
      const next: SelectedSalesUnit[] = [
        ...prev,
        { salesUnitId: unit.id, ownershipPct: 0, members: unit.members },
      ];
      const evenSplit = Math.floor(100 / next.length);
      const remainder = 100 - evenSplit * next.length;
      return next.map((o, i) => ({
        ...o,
        ownershipPct: evenSplit + (i === 0 ? remainder : 0),
      }));
    });
  };

  const applyOffer = (extracted: MatchedBudget) => {
    const aiValues: Record<string, { amount: number }> = {};
    const lines: BudgetLine[] = extracted.lines.map(item => {
      const lineId = String(Math.random());
      aiValues[lineId] = { amount: item.amount };
      return {
        id: lineId,
        // VIB-584: bruk effective_phase fra mal-match når tilgjengelig.
        // Linjer uten mal-match beholder parser-detected phase.
        phase: item.effective_phase,
        description: item.description,
        quantity_hours: item.quantity_hours ?? 0,
        amount: item.amount,
        estimated_cost: 0,
        has_time: item.has_time,
        cost_type: item.effective_cost_type,
        template_id: item.template_match?.template_id ?? null,
        template_name: item.template_match?.template_name ?? null,
        // VIB-1021: per-linje tilvalg-flagg (stemplet i OfferPicker ved
        // kombinering), med fallback til budsjett-nivå for ukombinerte budsjetter.
        is_optional: item.is_optional ?? extracted.is_optional ?? false,
      };
    });
    setBudgetLines(lines);
    setAiParsedValues(aiValues);
    if (extracted.offer_total > 0) setTotalAmount(String(extracted.offer_total));
    // VIB-686: hvis tilbudet har eksplisitt invoice_split, oversett til to-rad
    // (oppstart/levering)-terminliste. Mer komplekse milepæler legges til
    // manuelt i Fakturering-steget.
    if (extracted.invoice_split?.startup_pct) {
      const startupPct = extracted.invoice_split.startup_pct;
      setInvoiceTerms([
        { label: "Oppstart", pct: startupPct },
        { label: "Levering", pct: 100 - startupPct },
      ]);
    }
    setOfferOverheadPct(extracted.overhead_pct ?? 0);
    setOfferDiscountPct(extracted.discount_pct ?? 0);
    setOfferDiscountAmount(extracted.discount_amount ?? 0);
    setPdfUploaded(true);
  };

  return {
    step,
    setStep,
    stepErrors,
    customerId,
    setCustomerId,
    projectName,
    setProjectName,
    projectCode,
    setProjectCode,
    totalAmount,
    setTotalAmount,
    deadline,
    setDeadline,
    estimatedStartDate,
    setEstimatedStartDate,
    isInternal,
    setIsInternal,
    projectType,
    setProjectType,
    projectStatus,
    setProjectStatus,
    productionLeadId,
    setProductionLeadId,
    pdfUploaded,
    setPdfUploaded,
    budgetLines,
    setBudgetLines,
    aiParsedValues,
    setAiParsedValues,
    aiProgress,
    setAiProgress,
    aiLoading,
    setAiLoading,
    offerOverheadPct,
    setOfferOverheadPct,
    offerDiscountPct,
    setOfferDiscountPct,
    offerDiscountAmount,
    setOfferDiscountAmount,
    selectedSalesUnits,
    setSelectedSalesUnits,
    // VIB-686: invoiceTerms erstatter legacy invoiceModel/invoicePercent/invoiceFixed.
    invoiceTerms,
    setInvoiceTerms,
    invoiceTermsValid,
    startupPct,
    paymentDueDays,
    setPaymentDueDays,
    invoiceEmail,
    setInvoiceEmail,
    customerReferencePerson,
    setCustomerReferencePerson,
    customerReferenceNumber,
    setCustomerReferenceNumber,
    billsTravelPerMedgatt,
    setBillsTravelPerMedgatt,
    includeTravelInOverhead,
    selectedCustomer,
    total,
    startAmount,
    budgetTotal,
    totalEstCost,
    totalHours,
    calculatedTotal,
    validationDiff,
    validateStep,
    handleNext,
    toggleSalesUnit,
    applyOffer,
  };
}
